import * as React from 'react';
import { useEffect, useRef, useState } from 'react';

import PropTypes from 'prop-types';

import Retracked from 'js/app/retracked';

import { StyleSheet, css } from '@coursera/coursera-ui';

import Alert from 'bundles/authentication-common/components/Alert';
import Form from 'bundles/authentication-common/components/Form';
import Submit from 'bundles/authentication-common/components/Submit';
import * as actions from 'bundles/authentication-common/components/actions';
import EmailInput from 'bundles/authentication-common/components/inputs/EmailInput';
import NameInput from 'bundles/authentication-common/components/inputs/NameInput';
import PasswordInput from 'bundles/authentication-common/components/inputs/PasswordInput';
import {
  ARKOSE_SIGNUP_KEY,
  RECAPTCHA_ACTIONS,
  SIGNUP_REDESIGN_RECAPTCHA_ID,
} from 'bundles/authentication-common/constants/botManager';
import {
  EMAIL_CONSTRAINT,
  FIRSTNAME_CONSTRAINT,
  LASTNAME_CONSTRAINT,
  PASSWORD_CONSTRAINT,
} from 'bundles/authentication-common/constants/constraints';
import {
  emailRequirementsError,
  existingEmailError,
  generalErrorMessage,
  nameRequirementsError,
  passwordRequirementsError,
  responseMessageFactory,
} from 'bundles/authentication-common/constants/errors';
import useBotManager from 'bundles/authentication-common/hooks/useBotManager';
import { REGISTRATION_CONTEXT } from 'bundles/authentication-common/types';
import type { ErrorResponse, EventData, SignupSharedProps, SuccessResponse } from 'bundles/authentication-common/types';
import withSingleTracked from 'bundles/common/components/withSingleTracked';
import { INTERACTION_EVENTS_MAP } from 'bundles/expression-of-interest/constants';
import { useEoiEventing } from 'bundles/expression-of-interest/utils/eventingUtils';
import { trackAuthAttempts } from 'bundles/user-account/utils/authTrackingUtils';

import _t from 'i18n!nls/authentication';

type Context = {
  _eventData: EventData;
};

type Props = SignupSharedProps & {
  extraFormContent?: React.ReactElement;
  toFocus?: boolean;
  showSubmitButton?: boolean;
  trackingProductId: string;
  afterSubmit?: () => void;
  beforeSubmit?: () => void;
};

const TrackedSubmit = withSingleTracked({ type: 'BUTTON' })(Submit);

const styles = StyleSheet.create({
  container: {
    display: 'grid',
    gridGap: '12px',
    gridTemplateColumns: '1fr',
  },
  item: {
    ':nth-child(1)': {
      '-ms-grid-row': '1',
    },
    ':nth-child(2)': {
      '-ms-grid-row': '2',
    },
    ':nth-child(3)': {
      '-ms-grid-row': '3',
    },
    ':nth-child(4)': {
      '-ms-grid-row': '4',
    },
    ':nth-child(5)': {
      '-ms-grid-row': '5',
    },
  },
  reCAPTCHA: {
    display: 'flex',
    'justify-content': 'center',
    ':nth-of-type(n) .grecaptcha-badge': { display: 'none !important' },
  },
  disclaimer: {
    margin: '0 auto !important',
  },
  submit: {
    marginTop: '12px',
    width: '100%',
  },
});

const SignupForm = (
  {
    navigateToLogin,
    onSuccess,
    extraFormContent,
    toFocus,
    showSubmitButton = true,
    trackingProductId,
    afterSubmit,
    beforeSubmit,
  }: Props,
  { _eventData }: Context
) => {
  const [message, setMessage] = useState<string | JSX.Element>('');
  const [isRunning, setIsRunning] = useState(false);

  const firstNameInputRef = useRef<HTMLInputElement | null>(null);
  const lastNameInputRef = useRef<HTMLInputElement | null>(null);
  const nameInputRef = useRef<HTMLInputElement | null>(null);
  const emailInputRef = useRef<HTMLInputElement | null>(null);
  const passwordInputRef = useRef<HTMLInputElement | null>(null);

  const {
    isRetrievingTokenRef,
    getTokenData: getBotManagerTokenData,
    reset: resetBotManager,
  } = useBotManager({
    arkoseBotManager: {
      publicKey: ARKOSE_SIGNUP_KEY,
    },
    reCAPTCHA: {
      container: SIGNUP_REDESIGN_RECAPTCHA_ID,
      options: { action: RECAPTCHA_ACTIONS.signup },
    },
  });

  const { trackInteractEoi } = useEoiEventing(trackingProductId);
  const { v2TrackingName, v3EventKey } = INTERACTION_EVENTS_MAP.signupFormSubmitButton;

  useEffect(() => {
    firstNameInputRef.current = document.getElementById('firstname') as HTMLInputElement;
    lastNameInputRef.current = document.getElementById('lastname') as HTMLInputElement;
    emailInputRef.current = document.getElementById('email') as HTMLInputElement;
    passwordInputRef.current = document.getElementById('password') as HTMLInputElement;
    if (toFocus) {
      firstNameInputRef?.current?.focus();
    }
  }, [toFocus]);

  /**
   * Handles errors that occur during the signup process by resetting the reCAPTCHA,
   * setting an appropriate error message, updating the UI, and focusing the input field
   * associated with the error.
   *
   * @param {ErrorResponse} response - The error response object containing the error code and other relevant information.
   */
  const handleError = (response: ErrorResponse) => {
    resetBotManager();

    const { errorCode = '' } = response?.responseJSON || {};

    const messages = responseMessageFactory();

    if (messages[errorCode]) {
      if (errorCode === existingEmailError) {
        const errMessageForExistingAcc = messages[errorCode](navigateToLogin);
        setMessage(errMessageForExistingAcc);
        setIsRunning(false);
      } else {
        const msg = messages[errorCode as Exclude<keyof typeof messages, 'existingCourseraAccount'>];
        setMessage(msg);
        setIsRunning(false);
      }

      if (errorCode === nameRequirementsError) {
        nameInputRef.current?.focus();
      } else if (errorCode === existingEmailError || errorCode === emailRequirementsError) {
        emailInputRef.current?.focus();
      } else if (errorCode === passwordRequirementsError) {
        passwordInputRef.current?.focus();
      }
    } else {
      setMessage(_t('An unexpected error has occurred. Please try again'));
      setIsRunning(false);
    }
    afterSubmit?.();
  };

  /**
   * Handles a successful signup by setting a success message and calling the onSuccess function.
   *
   * @param {SuccessResponse} response - The success response object containing relevant information.
   *
   * @returns {ReturnType<typeof onSuccess>} The return value of the onSuccess function.
   */
  const handleSuccess = (response: SuccessResponse): ReturnType<typeof onSuccess> => {
    const successMsg = responseMessageFactory().success;

    setMessage(successMsg);

    return onSuccess(response);
  };

  /**
   * Handles the user signup process by validating the provided information, checking the reCAPTCHA token, and
   * calling the signup action. If the signup is successful, it handles the success response; otherwise, it handles
   * the error response.
   *
   * @param {Record<string, string>} values - An object containing the user's email, name, password, first name, and last name.
   * @returns {Promise<void | boolean | undefined>} Returns a promise that resolves to `undefined` when the function
   * execution is complete, `false` if the email domain is invalid, or a message if the reCAPTCHA token is missing.
   */
  const handleSignup = async (values: Record<string, string>): Promise<void | boolean | undefined> => {
    // Exit early if a token retrieval is in progress to prevent multiple function calls.
    if (isRetrievingTokenRef?.current) return false;

    const { email: signupEmail, name, password, firstName, lastName } = values;

    const fullName = name?.trim() ?? `${firstName?.trim()} ${lastName?.trim()}`;

    let tokenData;
    try {
      tokenData = await getBotManagerTokenData();
    } catch {
      const msg = generalErrorMessage();
      return setMessage(msg);
    }

    setIsRunning(true);
    beforeSubmit?.();
    trackAuthAttempts(_eventData, 'signup');

    try {
      const response = await actions.signup({
        email: signupEmail as string,
        name: fullName,
        password: password as string,
        registrationContext: REGISTRATION_CONTEXT.EOI,
        ...tokenData,
      });
      trackInteractEoi({
        eoiInteraction: v3EventKey,
      });

      handleSuccess(response as SuccessResponse);
    } catch (error) {
      handleError(error as ErrorResponse);
    }

    return undefined;
  };

  /**
   * Resets the displayed message when an input field is marked as invalid.
   */
  const onInvalid = () => {
    setMessage('');
  };

  /**
   * Tracks the focus event of a signup text input field for analytics purposes.
   *
   * @param {string} trackingName - The name of the input field being focused.
   */
  const onFocus = (trackingName: string) => {
    Retracked.trackComponent(_eventData, {}, trackingName, 'signup_text_input_focus');
  };

  /**
   * An object containing validation constraints for the firstName, lastName, email, and password fields.
   * Each constraint is a function that takes a string as an argument and returns a string
   * (indicating an error message) or a boolean (indicating that the input is valid).
   *
   * @type {Record<string, (arg: string) => string | boolean>}
   */
  const constraints: Record<string, (arg: string) => string | boolean> = {
    firstName: FIRSTNAME_CONSTRAINT,
    lastName: LASTNAME_CONSTRAINT,
    email: EMAIL_CONSTRAINT,
    password: PASSWORD_CONSTRAINT,
  };
  return (
    <Form
      {...css('rc-SignupForm', styles.container)}
      // @ts-expect-error FIXME result of fixing typing on coursera-ui aphrodite exports in PR#45351
      constraints={constraints}
      name="signup"
      onSubmit={handleSignup}
      onInvalid={onInvalid}
      id="eoi-modal-signup-form"
    >
      {message && (
        <Alert
          role="status"
          aria-live="assertive"
          {...css(styles.item)}
          type={message === responseMessageFactory().success ? 'success' : 'error'}
        >
          {message}
        </Alert>
      )}

      <NameInput
        label={_t('First Name')}
        name="firstName"
        placeholder={_t('Enter your first name')}
        ref={firstNameInputRef}
        onFocus={() => onFocus('first_name')}
        autoComplete="given-name"
        {...css(styles.item)}
      />
      <NameInput
        label={_t('Last Name')}
        name="lastName"
        placeholder={_t('Enter your last name')}
        ref={lastNameInputRef}
        onFocus={() => onFocus('last_name')}
        autoComplete="family-name"
        {...css(styles.item)}
      />

      <EmailInput
        {...css(styles.item)}
        // @ts-expect-error FIXME result of fixing typing on coursera-ui aphrodite exports in PR#45351
        emailSpellingCheck
        required
        ref={emailInputRef}
        onFocus={() => onFocus('email')}
      />
      <PasswordInput
        {...css(styles.item)}
        hint={_t('Between 8 and 72 characters')}
        ref={passwordInputRef}
        onFocus={() => onFocus('password')}
        autoComplete="new-password"
      />
      {extraFormContent}
      <div {...css(styles.reCAPTCHA)} id={SIGNUP_REDESIGN_RECAPTCHA_ID} data-size="invisible" />
      {showSubmitButton && (
        <TrackedSubmit
          {...css(styles.submit, styles.item, styles.submit)}
          data-catchpoint="signup-form-submit-button"
          data-e2e="signup-form-submit-button"
          disabled={isRunning}
          trackingName={v2TrackingName}
        >
          {_t('Sign Up')}
        </TrackedSubmit>
      )}
    </Form>
  );
};

SignupForm.contextTypes = {
  _eventData: PropTypes.object.isRequired,
};

export default Retracked.createTrackedContainer<Props>(() => ({
  namespace: {
    page: 'signup',
  },
}))(SignupForm);
